/**
 * Mauve Lane · cartão de kit (sections/velune-mm-kits.liquid)
 *
 * - Refaz a conta ("Value", "Kit price", "You save") e o nome da cor escolhida quando a cliente troca a
 *   variante de um item. A conta é a mesma da Shopify para o desconto automático da loja: o total é a soma
 *   com o desconto, arredondada ao centavo, com o meio centavo para cima.
 * - No envio, põe todas as variantes escolhidas no carrinho de uma vez (/cart/add.js com items) e dispara o
 *   mesmo evento de carrinho do tema (CartLinesUpdateEvent): a gaveta abre e se atualiza sozinha.
 * - Não aplica desconto nenhum: quem dá os 15% é o desconto automático da loja, no carrinho.
 * - Item sem estoque: o botão fica desligado, com o aviso de qual item acabou. Se a loja recusar a adição
 *   (estoque que acabou depois de a página abrir), a mensagem dela aparece no cartão.
 * - Item âncora (data-kit-anchor, só no cartão da página de produto, sections/velune-mm-product-kit.liquid):
 *   é o produto da própria página. Quando a cliente troca a cor ou a opção no seletor principal, o Horizon
 *   dispara o evento StandardEvents.productSelect ("shopify:product:select"), com uma promessa que traz a
 *   variante nova (detail.resource). O cartão ouve esse evento e marca a mesma variante no item âncora, e a
 *   conta se refaz. A troca feita dentro do cartão não mexe no seletor da página.
 * - A resposta do "Add" decide sozinha se deu certo. Se a leitura seguinte do carrinho (/cart.js) falhar, não
 *   aparece erro (os itens já entraram; um erro levaria a um segundo "Add"): tenta de novo e, falhando outra
 *   vez, abre a página do carrinho.
 * Sem este script o cartão continua funcionando: é um formulário comum, que leva à página do carrinho.
 */
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {{id: string, price: number, available: boolean, name: string, title: string}} KitChoice
 */

/** @returns {string[]} */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter((id) => typeof id === 'string' && id.length > 0);
}

/** @returns {Promise<any>} O carrinho como está na loja */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

class VeluneMmKit extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  #busy = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    if (this.querySelector('[data-kit-anchor]')) {
      document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    }
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get #pct() {
    const value = Number(this.dataset.pct);
    return value > 0 && value < 100 ? value : 0;
  }

  /** O Liquid grava os marcadores como [[amount]] para não brigar com as chaves dele. */
  get #moneyFormat() {
    return String(this.dataset.moneyFormat || '{{amount}}').replace(/\[\[(\w+)\]\]/g, '{{$1}}');
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.dataset.currency || 'USD');
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-kit-item]')));
  }

  /**
   * A variante escolhida de um item: a bolinha marcada, a linha da caixa de escolha ou, em produto de uma
   * variante só, o campo escondido. Sem nada marcado (produto todo esgotado) devolve null.
   * @param {HTMLElement} item
   * @returns {KitChoice | null}
   */
  #choice(item) {
    const title = item.dataset.title || '';
    const select = item.querySelector('select[data-kit-select]');

    if (select instanceof HTMLSelectElement) {
      const option = select.selectedOptions[0];
      if (!option || option.disabled) return null;
      return {
        id: option.value,
        price: Number(option.dataset.price) || 0,
        available: option.dataset.available !== 'false',
        name: option.textContent?.trim() || '',
        title,
      };
    }

    const input = item.querySelector('input[data-kit-choice]:checked, input[type="hidden"][data-kit-choice]');
    if (!(input instanceof HTMLInputElement)) return null;
    return {
      id: input.value,
      price: Number(input.dataset.price) || 0,
      available: input.dataset.available !== 'false',
      name: input.dataset.name || '',
      title,
    };
  }

  /** @param {string} role @param {string} text */
  #setText(role, text) {
    const element = this.querySelector(`[${role}]`);
    if (element && element.textContent !== text) element.textContent = text;
  }

  #render() {
    const button = this.querySelector('[data-kit-add]');
    let sum = 0;
    /** @type {string[]} */
    const soldOut = [];

    for (const item of this.#items) {
      const choice = this.#choice(item);
      if (!choice || !choice.available) {
        soldOut.push(item.dataset.title || '');
        // Para a soma não cair, entra o preço da primeira variante do item
        const first = item.querySelector('[data-price]');
        sum += first instanceof HTMLElement ? Number(first.dataset.price) || 0 : 0;
        continue;
      }

      sum += choice.price;

      const price = item.querySelector('[data-kit-item-price]');
      if (price) price.textContent = this.#money(choice.price);
      const name = item.querySelector('[data-kit-choice-name]');
      if (name && choice.name) name.textContent = choice.name;
    }

    // Total arredondado ao centavo com o meio centavo para cima (Math.round faz isso com números positivos)
    const total = Math.round((sum * (100 - this.#pct)) / 100);
    this.#setText('data-kit-value', this.#money(sum));
    this.#setText('data-kit-price', this.#money(total));
    this.#setText('data-kit-save', this.#money(sum - total));

    if (button instanceof HTMLButtonElement && !this.#busy) {
      const isSoldOut = soldOut.length > 0;
      // O texto do botão pode levar o preço do kit: "[price]" vira o total com o desconto (cartão simples da home)
      const label = (
        isSoldOut ? this.dataset.labelSoldOut || 'Sold out' : this.dataset.labelAdd || 'Add all to cart'
      ).replace('[price]', this.#money(total));
      button.disabled = isSoldOut;
      if (button.textContent?.trim() !== label) button.textContent = label;

      if (isSoldOut) {
        const text = this.dataset.textSoldOut || '[product] is sold out right now.';
        this.#showMessage(text.replace('[product]', soldOut.filter(Boolean).join(', ')));
      }
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('[data-kit-item]')) return;
    this.#hideMessage();
    this.#render();
  };

  /**
   * Troca de variante no seletor principal da página de produto: o item âncora acompanha.
   * O evento também sai dos cartões de produto e da compra rápida (outros produtos): esses não contam.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('product-card') || target.closest('velune-mm-kit')) return;

    // @ts-ignore -- ProductSelectEvent do Horizon traz a promessa com a variante nova
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((/** @type {any} */ result) => {
        const anchor = this.querySelector('[data-kit-anchor]');
        if (!(anchor instanceof HTMLElement)) return;

        const detail = result?.detail;
        const productId = detail?.productId ? String(detail.productId) : '';
        if (productId && anchor.dataset.productId && productId !== anchor.dataset.productId) return;

        const variantId = detail?.resource?.id;
        if (variantId) this.#selectVariant(anchor, String(variantId));
        else if (detail && detail.resource === null) this.#clearVariant(anchor);
      })
      .catch(() => {
        /* troca cancelada por outra mais nova */
      });
  };

  /**
   * Marca uma variante num item (bolinha ou linha da caixa de escolha) e refaz a conta. Variante esgotada
   * também é marcada: o botão passa a avisar que o item acabou, como na caixa de compra da página.
   * @param {HTMLElement} item
   * @param {string} variantId
   */
  #selectVariant(item, variantId) {
    const select = item.querySelector('select[data-kit-select]');

    if (select instanceof HTMLSelectElement) {
      const option = Array.from(select.options).find((candidate) => candidate.value === variantId);
      if (!option || select.value === variantId) return;
      select.value = variantId;
    } else {
      const inputs = /** @type {HTMLInputElement[]} */ (
        Array.from(item.querySelectorAll('input[type="radio"][data-kit-choice]'))
      );
      const input = inputs.find((candidate) => candidate.value === variantId);
      if (!input || input.checked) return;
      input.checked = true;
    }

    this.#hideMessage();
    this.#render();
  }

  /**
   * Combinação que não existe no seletor principal (produto de 2 ou mais opções): o item âncora fica sem
   * variante e o botão avisa, como a caixa de compra, que mostra "Unavailable".
   * @param {HTMLElement} item
   */
  #clearVariant(item) {
    const select = item.querySelector('select[data-kit-select]');
    if (!(select instanceof HTMLSelectElement) || select.selectedIndex === -1) return;
    select.selectedIndex = -1;
    this.#hideMessage();
    this.#render();
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-kit-form]')) return;
    event.preventDefault();
    this.#add(form);
  };

  /** @param {HTMLFormElement} form */
  async #add(form) {
    if (this.#busy) return;

    const choices = this.#items.map((item) => this.#choice(item));
    if (choices.some((choice) => !choice || !choice.available)) {
      this.#render();
      return;
    }

    // A mesma variante duas vezes vira uma linha com quantidade 2
    /** @type {Map<string, number>} */
    const groups = new Map();
    for (const choice of choices) {
      if (choice) groups.set(choice.id, (groups.get(choice.id) || 0) + 1);
    }
    const items = Array.from(groups, ([id, quantity]) => ({ id: Number(id), quantity }));
    const count = choices.length;

    const button = this.querySelector('[data-kit-add]');
    this.#busy = true;
    this.#hideMessage();
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const genericError = this.dataset.textError || 'Something went wrong. Please try again.';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
      });
      const result = await response.json();

      // A resposta do "Add" decide sozinha se deu certo; a leitura do carrinho vem depois e à parte
      // (o mesmo conserto de assets/velune-cart.js).
      if (!response.ok || result.status) {
        const message = result.description || result.message || genericError;
        this.#showMessage(message);
        form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        // O evento já saiu: a promessa dele fecha com o carrinho como está
        try {
          const cart = await fetchCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'velune-mm-kits' },
          });
        } catch (error) {
          deferred.reject(error);
        }
        return;
      }

      // A loja aceitou os itens. Se a leitura do carrinho falhar (rede), tenta mais uma vez; sem resposta, abre
      // a página do carrinho, como o formulário faria sem JavaScript. Mostrar erro aqui levaria a cliente a
      // tocar no botão de novo e pôr o kit duas vezes.
      let cart = null;
      for (let attempt = 0; attempt < 2 && !cart; attempt++) {
        cart = await fetchCart().catch(() => null);
      }
      if (!cart) {
        deferred.reject(new Error('Cart request failed'));
        window.location.assign(Theme.routes.cart_url);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: result.sections,
          source: 'velune-mm-kits',
          itemCount: count,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#showMessage(genericError);
      form.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#busy = false;
      if (button instanceof HTMLButtonElement && button.isConnected) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  }

  /** @param {string} message */
  #showMessage(message) {
    const element = this.querySelector('[data-kit-error]');
    if (!(element instanceof HTMLElement)) return;
    if (element.textContent !== message) element.textContent = message;
    element.hidden = false;
  }

  #hideMessage() {
    const element = this.querySelector('[data-kit-error]');
    if (!(element instanceof HTMLElement)) return;
    element.textContent = '';
    element.hidden = true;
  }
}

if (!customElements.get('velune-mm-kit')) {
  customElements.define('velune-mm-kit', VeluneMmKit);
}
